import {
  collection,
  getDocs,
  getFirestore,
  query,
  Timestamp,
  where,
  doc,
  getDoc,
  setDoc,
  addDoc,
  onSnapshot,
  orderBy,
  limit,
  updateDoc,
  arrayUnion,
  arrayRemove,
  QuerySnapshot,
  DocumentData,
} from "firebase/firestore"
import { getUser } from "services/auth"

type MessageType = "text" | "audio" | "photo" | "video" | "file"
type MessageStatus = "sent" | "delivered" | "read"
type ChatTypes = "private" | "group"

export type ChatType = {
  id?: string
  name?: string // for groups, optional
  description?: string // for groups, optional
  photoURL?: string // for groups, optional
  type: ChatTypes
  created: Timestamp
  updated?: Timestamp
  participants: string[] // uid for users involved in chat
  createdBy?: string // uid of creator (for groups)
  lastMessage?: {
    type: MessageType
    text?: string
    timestamp: Timestamp
    sender: string
  }
  unreadCount?: { [uid: string]: number } // unread count per user
}

type CreateMessageProps = {
  chatId: string
  text?: string
  type: MessageType
  attachment?: string[]
}

type CreateGroupProps = {
  name: string
  description?: string
  photoURL?: string
  participants: string[] // uids of initial members
}

//Sub-collection of Chat
export type Message = {
  id?: string //firebase document id
  type: MessageType
  chatId: string //id of chat
  sender: string //uid of sender
  text?: string //optional, message can just be audio/text
  created: Timestamp
  updated?: Timestamp
  status: MessageStatus
  readBy?: string[] //for groups, list of uid that read message
  attachment?: string[] //audio/video/image/file url
}

type GetChatProps = { uid: string; id?: string } | { id: string; uid?: string }

export type UserType = {
  headshot: string
  last_online: Timestamp
  name: string
  state: "offline" | "online"
  uid: string
}

// ============= CHAT OPERATIONS =============

export async function createPrivateChat(participant: string) {
  const user = await getUser()
  if (!user) return null

  // Check if chat already exists
  const existingChat = await getChat({ uid: participant })
  if (existingChat) return existingChat

  const document = doc(collection(getFirestore(), `chats`))
  const chat: ChatType = {
    id: document.id,
    type: "private",
    created: Timestamp.now(),
    participants: [user.uid, participant],
    unreadCount: { [user.uid]: 0, [participant]: 0 },
  }
  await setDoc(document, chat)
  return chat
}

export async function getChat(props: GetChatProps) {
  const user = await getUser()
  if (!user) return null

  if (props.uid) {
    // Try both participant orders for private chats
    const q1 = query(
      collection(getFirestore(), `chats`),
      where("participants", "==", [user.uid, props.uid]),
      where("type", "==", "private"),
    )
    const q2 = query(
      collection(getFirestore(), `chats`),
      where("participants", "==", [props.uid, user.uid]),
      where("type", "==", "private"),
    )

    const [snapshot1, snapshot2] = await Promise.all([getDocs(q1), getDocs(q2)])

    if (!snapshot1.empty) return snapshot1.docs[0].data() as ChatType
    if (!snapshot2.empty) return snapshot2.docs[0].data() as ChatType

    return null
  }

  if (props.id) {
    const document = doc(getFirestore(), `chats/${props.id}`)
    const snapshot = await getDoc(document)

    if (!snapshot.exists()) return null

    return { ...snapshot.data(), id: snapshot.id } as ChatType
  }
}

export async function getUserChats() {
  const user = await getUser()
  if (!user) return []

  const q = query(
    collection(getFirestore(), `chats`),
    where("participants", "array-contains", user.uid),
    orderBy("updated", "desc"),
  )

  const snapshot = await getDocs(q)
  return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as ChatType))
}

export function onChatsSnapshot(
  callback: (chats: ChatType[]) => void,
  onError?: (error: Error) => void,
) {
  const user = getUser()
  if (!user) return () => { }

  user.then(u => {
    const q = query(
      collection(getFirestore(), `chats`),
      where("participants", "array-contains", u.uid),
      orderBy("updated", "desc"),
    )

    return onSnapshot(
      q,
      snapshot => {
        const chats = snapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id,
        } as ChatType))
        callback(chats)
      },
      onError,
    )
  })

  return () => { }
}

export async function updateChatLastMessage(
  chatId: string,
  message: Message,
) {
  const chatRef = doc(getFirestore(), `chats/${chatId}`)
  await updateDoc(chatRef, {
    lastMessage: {
      type: message.type,
      text: message.text,
      timestamp: message.created,
      sender: message.sender,
    },
    updated: Timestamp.now(),
  })
}

// ============= MESSAGE OPERATIONS =============

export async function sendMessage(props: CreateMessageProps) {
  const user = await getUser()
  if (!user) return null

  const messagesRef = collection(getFirestore(), `chats/${props.chatId}/messages`)

  const message: Omit<Message, "id"> = {
    type: props.type,
    chatId: props.chatId,
    sender: user.uid,
    text: props.text,
    created: Timestamp.now(),
    status: "sent",
    ...(props.attachment && { attachment: props.attachment }),
    readBy: [user.uid],
  }

  const docRef = await addDoc(messagesRef, message)
  const createdMessage = { ...message, id: docRef.id } as Message

  // Update chat's last message
  await updateChatLastMessage(props.chatId, createdMessage)

  return createdMessage
}

export async function getMessages(chatId: string, limitCount: number = 50) {
  const messagesRef = collection(getFirestore(), `chats/${chatId}/messages`)
  const q = query(messagesRef, orderBy("created", "desc"), limit(limitCount))

  const snapshot = await getDocs(q)
  return snapshot.docs
    .map(doc => ({ ...doc.data(), id: doc.id } as Message))
    .reverse() // Reverse to show oldest first
}

export function onMessagesSnapshot(
  chatId: string,
  callback: (messages: Message[]) => void,
  onError?: (error: Error) => void,
) {
  const messagesRef = collection(getFirestore(), `chats/${chatId}/messages`)
  const q = query(messagesRef, orderBy("created", "asc"))

  return onSnapshot(
    q,
    snapshot => {
      const messages = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
      } as Message))
      callback(messages)
    },
    onError,
  )
}

export async function updateMessageStatus(
  chatId: string,
  messageId: string,
  status: MessageStatus,
) {
  const messageRef = doc(getFirestore(), `chats/${chatId}/messages/${messageId}`)
  await updateDoc(messageRef, { status })
}

export async function markMessagesAsRead(chatId: string) {
  const user = await getUser()
  if (!user) return

  const messagesRef = collection(getFirestore(), `chats/${chatId}/messages`)
  const q = query(messagesRef, where("sender", "!=", user.uid))

  const snapshot = await getDocs(q)

  const updates = snapshot.docs.map(doc => {
    const messageRef = doc.ref
    return updateDoc(messageRef, {
      readBy: arrayUnion(user.uid),
      status: "read",
    })
  })

  await Promise.all(updates)

  // Reset unread count for current user
  const chatRef = doc(getFirestore(), `chats/${chatId}`)
  await updateDoc(chatRef, {
    [`unreadCount.${user.uid}`]: 0,
  })
}

// ============= GROUP OPERATIONS =============

export async function createGroup(props: CreateGroupProps) {
  const user = await getUser()
  if (!user) return null

  const document = doc(collection(getFirestore(), `chats`))

  const chat: ChatType = {
    id: document.id,
    type: "group",
    name: props.name,
    description: props.description,
    photoURL: props.photoURL,
    created: Timestamp.now(),
    updated: Timestamp.now(),
    participants: [user.uid, ...props.participants],
    createdBy: user.uid,
    unreadCount: {},
  }

  // Initialize unread count for all participants
  chat.participants.forEach(uid => {
    if (chat.unreadCount) chat.unreadCount[uid] = 0
  })

  await setDoc(document, chat)
  return chat
}

export async function addGroupParticipants(chatId: string, participants: string[]) {
  const chatRef = doc(getFirestore(), `chats/${chatId}`)

  const updates: any = {
    participants: arrayUnion(...participants),
    updated: Timestamp.now(),
  }

  // Initialize unread count for new participants
  participants.forEach(uid => {
    updates[`unreadCount.${uid}`] = 0
  })

  await updateDoc(chatRef, updates)
}

export async function removeGroupParticipant(chatId: string, participantUid: string) {
  const chatRef = doc(getFirestore(), `chats/${chatId}`)
  await updateDoc(chatRef, {
    participants: arrayRemove(participantUid),
    updated: Timestamp.now(),
  })
}

export async function updateGroupInfo(
  chatId: string,
  info: { name?: string; description?: string; photoURL?: string },
) {
  const chatRef = doc(getFirestore(), `chats/${chatId}`)
  await updateDoc(chatRef, {
    ...info,
    updated: Timestamp.now(),
  })
}

export async function leaveGroup(chatId: string) {
  const user = await getUser()
  if (!user) return

  await removeGroupParticipant(chatId, user.uid)
}

// Legacy function for backwards compatibility
export async function createMessage(props: CreateMessageProps) {
  return sendMessage(props)
}
